/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { formatDate } from '@angular/common';
import { JoinStateDetails, JoinStateType } from '@app/app/shared/models';
import { JoinAction } from '@app/app/shared/services/join/join-data.service';

const KEY = 'translations.join';

export interface JoinText {
  key: string;
  params?: Record<string, string | number>;
}

export type JoinBadgeTone = 'warning' | 'success' | 'accent' | 'neutral';

export interface JoinButton {
  action: JoinAction;
  labelKey: string;
}

/** What a content card shows until the learner is enrolled: a badge above the title, a caption and one step forward. */
export interface JoinCardView {
  badge: (JoinText & { tone: JoinBadgeTone }) | null;
  caption: JoinText;
  button: (JoinButton & { primary: boolean }) | null;
}

/** The join bar on a content page: going back (withdraw, leave, decline) only happens here. */
export interface JoinBarView {
  state: JoinStateType;
  head: JoinText;
  sub: JoinText[];
  primary: JoinButton | null;
  secondary: JoinButton | null;
  /** Offers that expire draw the eye. */
  urgent: boolean;
}

/** Formats an API instant for display: `date` for a day, `dateTime` for a day and time. */
export interface JoinDates {
  date(iso: string): string;
  dateTime(iso: string): string;
}

export function joinDates(lang: string | undefined): JoinDates {
  const locale = !lang || lang === 'en' ? 'en-GB' : lang;
  return {
    date: (iso) => formatDate(iso, 'd MMM', locale),
    dateTime: (iso) => formatDate(iso, 'd MMM, HH:mm', locale),
  };
}

/**
 * Whether the learner still has something to do, or know, about joining; enrolled learners see their progress instead.
 * Content they can enrol on themselves enrols them when they open it, so there's nothing to do first.
 */
export function isJoinPending(join: JoinStateDetails | null | undefined): join is JoinStateDetails {
  return !!join && join.state !== 'ENROLLED' && join.state !== 'NO_ACCESS' && join.state !== 'CAN_ENROL';
}

/**
 * The registration form the learner fills in before taking [action], while they haven't: requesting a place, and
 * accepting an invitation to content with a registration step, which requests one.
 */
export function registrationFormBefore(join: JoinStateDetails | null | undefined, action: JoinAction): string | null {
  return action === 'REGISTER' || action === 'ACCEPT_INVITATION' ? (join?.registrationFormUid ?? null) : null;
}

export function joinCardView(join: JoinStateDetails | null | undefined, dates: JoinDates): JoinCardView | null {
  if (!isJoinPending(join)) {
    return null;
  }
  const card = `${KEY}.card`;
  const badge = (name: string, tone: JoinBadgeTone, params?: JoinText['params']) => ({ key: `${card}.badge.${name}`, params, tone });
  const caption = (name: string, params?: JoinText['params']) => ({ key: `${card}.caption.${name}`, params });
  const button = (action: JoinAction, name: string, primary = true) => ({ action, labelKey: `${KEY}.action.${name}`, primary });
  switch (join.state) {
    case 'CAN_REGISTER':
      return {
        badge: null,
        caption:
          join.seatsLeft != null
            ? caption('seatsLeft', { count: join.seatsLeft })
            : join.registrationClosesAt
              ? caption('closes', { date: dates.date(join.registrationClosesAt) })
              : caption('registrationOpen'),
        button: button('REGISTER', 'requestPlace'),
      };
    case 'REGISTRATION_NOT_OPEN':
      return join.registrationOpensAt
        ? {
            badge: badge('opens', 'neutral', { date: dates.date(join.registrationOpensAt) }),
            caption: caption('registrationOpens', { date: dates.dateTime(join.registrationOpensAt) }),
            button: null,
          }
        : { badge: badge('notOpen', 'neutral'), caption: caption('registrationNotOpen'), button: null };
    case 'REGISTRATION_CLOSED':
      return { badge: badge('registrationClosed', 'neutral'), caption: caption('noMoreRequests'), button: null };
    case 'FULL':
      return { badge: badge('full', 'neutral'), caption: caption('noWaitingList'), button: null };
    case 'CAN_JOIN_WAITING_LIST':
      return { badge: badge('full', 'neutral'), caption: caption('allSeatsTaken'), button: button('REGISTER', 'joinWaitingList', false) };
    case 'PENDING_APPROVAL':
      return { badge: badge('awaitingApproval', 'warning'), caption: caption('waitingForDecision'), button: null };
    case 'WAITLISTED':
      return {
        badge: badge('waitingList', 'accent'),
        caption: join.waitlistPosition ? caption('waitlistPosition', { position: join.waitlistPosition }) : caption('onWaitingList'),
        button: null,
      };
    case 'SEAT_OFFERED':
      return {
        badge: badge('seatOffered', 'success'),
        caption: join.expiresAt ? caption('acceptBy', { date: dates.dateTime(join.expiresAt) }) : caption('seatFree'),
        button: button('ACCEPT_SEAT', 'acceptSeat'),
      };
    case 'INVITED':
      return {
        badge: badge('invited', 'accent'),
        caption: join.expiresAt ? caption('expires', { date: dates.date(join.expiresAt) }) : caption('invited'),
        button: button('ACCEPT_INVITATION', 'accept'),
      };
    default:
      return null;
  }
}

/** @param manualApproval - whether an approver decides each request, from the content's registration settings */
export function joinBarView(join: JoinStateDetails | null | undefined, manualApproval: boolean, dates: JoinDates): JoinBarView | null {
  if (!isJoinPending(join)) {
    return null;
  }
  const bar = `${KEY}.bar`;
  const text = (name: string, params?: JoinText['params']) => ({ key: `${bar}.${name}`, params });
  const button = (action: JoinAction, name: string) => ({ action, labelKey: `${KEY}.action.${name}` });
  const view = (head: JoinText, sub: JoinText[], primary: JoinButton | null = null, secondary: JoinButton | null = null) => ({
    state: join.state,
    head,
    sub: primary && registrationFormBefore(join, primary.action) ? [...sub, text('formFirst')] : sub,
    primary,
    secondary,
    urgent: join.state === 'SEAT_OFFERED' || join.state === 'INVITED',
  });
  switch (join.state) {
    case 'CAN_REGISTER':
      return view(
        join.seatsLeft != null ? text('registrationOpenSeats', { count: join.seatsLeft }) : text('registrationOpen'),
        [
          ...(join.registrationClosesAt ? [text('closes', { date: dates.dateTime(join.registrationClosesAt) })] : []),
          ...(manualApproval ? [text('approverDecides')] : []),
        ],
        button('REGISTER', manualApproval ? 'requestPlace' : 'register'),
      );
    case 'REGISTRATION_NOT_OPEN':
      return view(
        join.registrationOpensAt
          ? text('registrationOpens', { date: dates.dateTime(join.registrationOpensAt) })
          : text('registrationNotOpen'),
        join.registrationClosesAt ? [text('closes', { date: dates.dateTime(join.registrationClosesAt) })] : [],
      );
    case 'REGISTRATION_CLOSED':
      return view(text('registrationClosed'), [text('contactOrganisers')]);
    case 'FULL':
      return view(text('allSeatsTaken'), [text('noWaitingList')]);
    case 'CAN_JOIN_WAITING_LIST':
      return view(text('allSeatsTaken'), [text('waitingListOffer')], button('REGISTER', 'joinWaitingList'));
    case 'PENDING_APPROVAL':
      return view(text('awaitingApproval'), [text('emailWhenDecided')], null, button('WITHDRAW', 'withdrawRequest'));
    case 'WAITLISTED':
      return view(
        join.waitlistPosition ? text('waitlistPosition', { position: join.waitlistPosition }) : text('onWaitingList'),
        [text('emailWhenSeatFree')],
        null,
        button('WITHDRAW', 'leaveWaitingList'),
      );
    case 'SEAT_OFFERED':
      return view(
        text('seatFree'),
        join.expiresAt ? [text('acceptBy', { date: dates.dateTime(join.expiresAt) })] : [],
        button('ACCEPT_SEAT', 'acceptSeat'),
        button('DECLINE_SEAT', 'decline'),
      );
    case 'INVITED':
      return view(
        text('invited'),
        join.expiresAt ? [text('invitationExpires', { date: dates.dateTime(join.expiresAt) })] : [],
        button('ACCEPT_INVITATION', 'acceptInvitation'),
        button('DECLINE_INVITATION', 'decline'),
      );
    default:
      return null;
  }
}

const REFUSALS = [
  'SELF_ENROLMENT',
  'REGISTRATION_REQUIRED',
  'REGISTRATION_NOT_OPEN',
  'REGISTRATION_CLOSED',
  'FULL',
  'DATA_FORM_NOT_COMPLETED',
  'INVITATION_TO_ACCEPT',
  'ALREADY_REQUESTED',
  'NO_SEAT_OFFER',
  'SEAT_OFFER_EXPIRED',
  'NO_INVITATION',
  'INVITATION_EXPIRED',
];

/** The API refuses a join with its reason as the message; anything else gets the generic failure. */
export function joinRefusalKey(message: string | null | undefined): string {
  return message && REFUSALS.includes(message) ? `${KEY}.refusal.${message}` : `${KEY}.refusal.failed`;
}
